import { FLOALA_TOOLBAR_COLLAPSED, FLOALA_TOOLBAR_EXPANDED, FROALA_CONFIG } from "@affinity/constants/froalaConfig";
import { colours } from "@affinity/constants/StyleConstants";
import { breakpointWidth, rem } from "@affinity/constants/StyleHelpers";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import parse from "html-react-parser";
import React, { FC } from "react";
import styled from "styled-components";

export const fields = {
    image: "image",
    content: "content",
    author: "author",
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.content]: string;
        [fields.author]: string;
    };
}

export const Testimonial: FC<OwnProps> = ({ fields }) => {
    return (
        <Section>
            {fields.image && (
                <ImageWrapper>
                    <Image src={fields.image} alt="" />
                </ImageWrapper>
            )}
            <TextWrapper>
                <Blockquote>{parse(fields.content, parserOptions)}</Blockquote>
                <Figcaption>{fields.author}</Figcaption>
            </TextWrapper>
        </Section>
    );
};

export const Section = styled.section`
    position: relative;
    z-index: 0;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
`;

const TextWrapper = styled.figure`
    grid-column: 1 / span 1;
    grid-row: 1 / span 1;
    position: relative;
    z-index: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;
    background-color: ${colours.white};
    gap: ${rem(10)};
    padding: ${rem(20)};
    justify-self: stretch;
    align-self: end;
    border-radius: 10px;
    margin-left: 0;
    margin-right: 0;
    margin-top: ${rem(300)};
    margin-bottom: ${rem(100)};
    ::after {
        position: absolute;
        top: 100%;
        right: ${rem(20)};
        border-top: ${rem(35)} solid ${colours.white};
        border-right: ${rem(35)} solid transparent;
        display: block;
        content: "";
    }
    @media (min-width: ${breakpointWidth("md")}) {
        margin-top: ${rem(200)};
    }
    @media (min-width: ${breakpointWidth("lg")}) {
        padding: ${rem(40)};
        margin-top: ${rem(200)};
        margin-bottom: ${rem(200)};
        align-self: center;
        justify-self: flex-start;
        max-width: 45%;
        border-radius: 15px;
        ::after {
            right: ${rem(40)};
            border-top-width: ${rem(50)};
            border-right-width: ${rem(50)};
        }
    }
`;

const Blockquote = styled.blockquote`
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: ${rem(10)};
    p {
        font-weight: 300;
        margin: 0;
        @media (min-width: ${breakpointWidth("lg")}) {
            font-size: ${rem(18)};
        }
    }
`;

const Figcaption = styled.figcaption`
    font-weight: 600;
    font-size: ${rem(14)};
`;

const ImageWrapper = styled.div`
    grid-column: 1 / span 1;
    grid-row: 1 / span 1;
    height: 100%;
    width: 100vw;
    margin-right: calc((100% - 100vw) / 2);
    margin-left: calc((100% - 100vw) / 2);
`;

const Image = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const quote: WidgetModule = {
    component: Testimonial,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
            },
            {
                fieldType: "Translatable",
                name: fields.content,
                editorTemplate: "RichTextField",
                displayName: "Content",
                defaultValue: "",
                extendedConfig: {
                    height: 300,
                    ...FROALA_CONFIG,
                },
                collapsedToolbarButtons: FLOALA_TOOLBAR_COLLAPSED,
                expandedToolbarButtons: FLOALA_TOOLBAR_EXPANDED,
                isRequired: true,
            },
            {
                fieldType: "Translatable",
                name: fields.author,
                editorTemplate: "TextField",
                displayName: "Author",
                defaultValue: "",
            },
        ],
    },
};

export default quote;
